import { useContext, useEffect, useMemo, useState } from "react";
import "../../../styles/Trends.css";
import SelectContext from "../../../utils/Context/SelectContext";
import { useFetchFunction } from "../../../utils/hooks/UseFetchUrl";
import { getScoreConfig } from "../../../utils/hooks/useScoreScale";
import { Spin, Tooltip, Typography } from "antd";
import { Outlet } from "react-router-dom";
import useClientAdmin from "../../../utils/hooks/IsClientAdmin";
import { VerticalAlignBottomOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import BarChart from "../../../components/chart-bars/BarChart";

const { Title } = Typography;

const NO_DATA_TEXT = "No Store Performance data available";
const LOAD_ERROR_TEXT = "Unable to load Store Performance data";

// ============================================== Excel styles =================================================
const thinBorder = { style: "thin", color: { rgb: "D9D9D9" } };
const cellBorder = {
  top: thinBorder,
  bottom: thinBorder,
  left: thinBorder,
  right: thinBorder,
};

const sectionTitleStyle = {
  font: { bold: true, sz: 14, color: { rgb: "000000" } },
};

const headerStyle = {
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  fill: { fgColor: { rgb: "EDEDED" } },
  border: cellBorder,
};

const maxMarksStyle = {
  alignment: { horizontal: "center", vertical: "center" },
  font: { bold: true, color: { rgb: "000000" } },
};

const dataCellStyle = (rowBg, col) => ({
  alignment: {
    vertical: "center",
    wrapText: true,
    horizontal: col === 1 ? "left" : "center",
  },
  fill: { fgColor: { rgb: rowBg } },
  border: cellBorder,
});

// ============================================== Score helpers =================================================
const getMarks = (row, cycleIndex) => row.scores?.[cycleIndex]?.marks ?? null;

// A question is shown (and exported) when it carries marks, has a score for
// every audit cycle and was answered in at least one of them.
const isValidRow = (row, cycleCount) =>
  (row.max_marks !== 0 || row.scores?.some((score) => score.marks > 0)) &&
  row.scores?.length >= cycleCount &&
  !row.scores.every((score) => score.marks === null);

// Colour for a question's marks, taken from the configured score scale using
// the marks as a percentage of the question's maximum. Null marks get no colour.
const getMarksColor = (marks, maxMarks, colorScale) => {
  if (marks === null) return null;
  const percentage = maxMarks > 0 ? (marks / maxMarks) * 100 : marks > 0 ? 100 : 0;
  return getScoreConfig(Math.floor(Math.min(percentage, 100)), colorScale).color;
};

const toExcelRgb = (color) => color.replace("#", "").toUpperCase();

const Trends = ({ storeData }) => {
  const storeId = storeData?.id;
  const colorScale = storeData?.client?.score_scale;
  const { selectedOption, setSelectedOption } = useContext(SelectContext);
  const isClientAdmin = useClientAdmin();
  const [selectedId, setSelectedId] = useState(null);

  // ============================================== Data fetching =================================================
  const { data: queTypesDataList, getData: getQueTypes } = useFetchFunction();
  const {
    data: markingGraphData,
    setData: setMarkingGraphData,
    getData: getMarkingGraph,
    loading: isGraphLoading,
    error: graphError,
  } = useFetchFunction();
  const {
    data: markingTablesData,
    setData: setMarkingTablesData,
    getData: getMarkingTables,
    loading: isTablesLoading,
    error: tablesError,
  } = useFetchFunction();
  const isLoading = isGraphLoading || isTablesLoading;

  useEffect(() => {
    if (storeId) {
      getQueTypes(`/store/${storeId}/questionnaire_types_list`);
    }
  }, [storeId]);

  // Each useFetchFunction instance aborts its previous request, so a slower
  // response for an earlier questionnaire never overwrites the current one.
  useEffect(() => {
    if (!selectedId || !storeId) return;
    const basePath = `report/questionnaire_type/${selectedId}/store/${storeId}`;
    getMarkingGraph(`${basePath}/marking_graph`);
    getMarkingTables(`${basePath}/marking`);
  }, [selectedId, storeId]);

  // ============================================== Questionnaire selection =================================================
  const questionnaireTypes = Array.isArray(queTypesDataList)
    ? queTypesDataList
    : [];

  useEffect(() => {
    if (Array.isArray(queTypesDataList) && queTypesDataList.length > 0) {
      const defaultId = queTypesDataList[0].id;
      setSelectedId(defaultId);
      setSelectedOption(defaultId.toString());
    }
  }, [queTypesDataList]);

  const handleSelectChange = (event) => {
    // Clear the previous questionnaire's results so they aren't shown while the new ones load.
    setMarkingGraphData([]);
    setMarkingTablesData([]);
    setSelectedId(parseInt(event.target.value, 10));
    setSelectedOption(event.target.value);
  };

  // ============================================== Table data =================================================
  const auditCycles = Array.isArray(markingTablesData?.audit_cycle)
    ? markingTablesData.audit_cycle
    : [];

  // Questions grouped by section_sequence, keeping only valid rows. Used by both
  // the tables and the Excel export so they always contain the same data.
  const sections = useMemo(() => {
    const scores = markingTablesData?.scores;
    const cycles = markingTablesData?.audit_cycle;
    if (!Array.isArray(scores) || !Array.isArray(cycles)) return [];

    const grouped = scores.reduce((acc, score) => {
      if (!acc[score.section_sequence]) {
        acc[score.section_sequence] = [];
      }
      acc[score.section_sequence].push(score);
      return acc;
    }, {});

    return Object.keys(grouped)
      .map((sectionSequence) => {
        const allRows = grouped[sectionSequence];
        return {
          sectionSequence,
          sectionName: allRows[0]?.section_name || "NA",
          hasMarkedQuestion: allRows.some((row) => row.max_marks !== 0),
          rows: allRows.filter((row) => isValidRow(row, cycles.length)),
        };
      })
      .filter((section) => section.hasMarkedQuestion && section.rows.length > 0);
  }, [markingTablesData]);

  // ============================================== Excel report =================================================
  const handleDownloadReport = () => {
    if (sections.length === 0) return;

    const worksheetData = [];
    const cellStyles = [];
    const addRow = (values, getStyle) => {
      const r = worksheetData.length;
      worksheetData.push(values);
      values.forEach((_, c) => {
        const s = getStyle(c);
        if (s) cellStyles.push({ r, c, s });
      });
    };

    sections.forEach((section) => {
      addRow([section.sectionName], () => sectionTitleStyle);
      addRow(
        ["Sr. No.", "Questions", "Max Marks", ...auditCycles],
        () => headerStyle,
      );

      section.rows.forEach((row, rowIndex) => {
        const rowBg = rowIndex % 2 === 0 ? "FFFFFF" : "F2F2F2";
        const marksByCycle = auditCycles.map((_, i) => getMarks(row, i));

        addRow(
          [
            row.sequence,
            row.question_txt,
            row.max_marks,
            ...marksByCycle.map((marks) => (marks !== null ? marks : "")),
          ],
          (col) => {
            if (col === 2) return maxMarksStyle;
            const marks = col >= 3 ? marksByCycle[col - 3] : null;
            if (marks === null) return dataCellStyle(rowBg, col);
            return {
              ...dataCellStyle(rowBg, col),
              fill: {
                fgColor: {
                  rgb: toExcelRgb(getMarksColor(marks, row.max_marks, colorScale)),
                },
              },
              font: { bold: true, color: { rgb: "ffffff" } },
            };
          },
        );
      });

      // Empty space between tables
      worksheetData.push([], []);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    worksheet["!cols"] = [
      { wch: 10 },
      { wch: 70 },
      { wch: 15 },
      ...auditCycles.map(() => ({ wch: 15 })),
    ];
    cellStyles.forEach(({ r, c, s }) => {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) worksheet[cellRef].s = s;
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Store Performance");
    XLSX.writeFile(workbook, "Store_Performance_Report.xlsx");
  };

  const isDownloadDisabled = isLoading || sections.length === 0;

  // ============================================== Render =================================================
  if (!isClientAdmin) {
    return (
      <div className="trends-container">
        <Title level={3} className="textNonAdmin">
          No reports for this store only completed reports graph will show up
          here
        </Title>
      </div>
    );
  }

  return (
    <div className="px-4 my-3 trends-container">
      <div className="quesnSele select gap-3 d-flex">
        <p className="text-start trendsQuesType">Questionnaire Type :</p>
        <select value={selectedOption} onChange={handleSelectChange}>
          {questionnaireTypes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      {/* Graph */}
      <div className="audit-details d-flex">
        <p className="m-0">Store Performance</p>
      </div>
      <div className="dashSecHero justify-content-center align-items-center row d-flex">
        <div className="secHeroLeft col-sm-14">
          {isLoading ? (
            <div className="spinner">
              <Spin size="smaller"></Spin>
            </div>
          ) : markingGraphData?.scores?.length > 0 ? (
            <div style={{ width: "100%", overflowX: "auto" }}>
              <div style={{ minWidth: "600px" }}>
                <BarChart
                  scores={markingGraphData.scores}
                  auditCycle={markingGraphData.audit_cycle}
                  maxMarks={markingGraphData.max_marks}
                  colorCodes={markingGraphData.color_codes}
                  isInteractive={true}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-4  noDataText">
              {graphError ? LOAD_ERROR_TEXT : NO_DATA_TEXT}
            </div>
          )}
        </div>
      </div>

      {/* Performance tables */}
      <div className="store-details">
        <div className="audit-details d-flex">
          <p className="m-0" style={{ color: "#4B5563" }}>
            Store Performance Details
          </p>
          <button
            className="download-report-btn"
            onClick={handleDownloadReport}
            disabled={isDownloadDisabled}
            style={{ opacity: isDownloadDisabled ? 0.5 : 1 }}
          >
            <Tooltip title="Export List">
              <VerticalAlignBottomOutlined className="exportIconAT" />
            </Tooltip>
            Download Report
          </button>
        </div>
        {isLoading ? (
          <div className="spinner">
            <Spin size="medium" />
          </div>
        ) : sections.length > 0 ? (
          sections.map((section, tableIndex) => (
            <div
              key={section.sectionSequence}
              className={`table${tableIndex + 1} my-4`}
            >
              <div className="customerArr">
                <p>{section.sectionName}</p>
              </div>
              <div className="fifthTable table-responsive">
                <table>
                  <thead>
                    <tr className="trends-row">
                      <th className="SNOTrends">Sr. No.</th>
                      <th className="QuesTrends">Questions</th>
                      <th className="maxMarksTrends">Max Marks</th>
                      {auditCycles.map((cycle) => (
                        <th key={cycle}>
                          <div>
                            <p className="my-2">{cycle}</p>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row) => (
                      <tr key={row.sequence} className="trendsDataRow">
                        <td className="SNOTrends">{row.sequence}</td>
                        <td className="text-start QuesTrends">
                          {row.question_txt}
                        </td>
                        <td className="maxMarksTrends">
                          <div className="maxMarksCenterText">
                            {row.max_marks}
                          </div>
                        </td>
                        {auditCycles.map((cycle, cycleIndex) => {
                          const marks = getMarks(row, cycleIndex);
                          return (
                            <td key={cycle} className="competitionAudits">
                              <div className="scoreIcons dfTrends flex-column">
                                <span>{marks !== null ? marks : "N/A"}</span>
                                <div
                                  className="dash1Trends"
                                  style={{
                                    background:
                                      getMarksColor(marks, row.max_marks, colorScale) ||
                                      "transparent",
                                  }}
                                ></div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center p-4  noDataText">
            {tablesError ? LOAD_ERROR_TEXT : NO_DATA_TEXT}
          </div>
        )}
      </div>
      <Outlet />
    </div>
  );
};

export default Trends;
